import React, {
  CSSProperties,
  HTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import FormElementErrorMessage from "@/components/botanic/FormElementErrorMessage";
import FormElementLabel from "@/components/botanic/FormElementLabel";
import FormField from "@/components/FormField";

type TextAreaProps = {
  /**
   * Will set the label to active state on initial load if you are anticipating a form input value to be pre populated
   */
  activeOnLoad?: boolean;
  className?: string;
  /**
   * Error message displayed below a form field. Also acts as the boolean to show the text
   */
  errorMessage?: string;
  height?: CSSProperties["width"];
  /**
   * Small explanatory or disclaimer text displayed below a form field
   */
  helperText?: string;
  id: string;
  label?: string;
  required?: boolean;
  /**
   * Resize property value https://developer.mozilla.org/en-US/docs/Web/CSS/resize
   */
  resize?: CSSProperties["resize"];
  textareaClasses?: string;
  /**
   * Use for form fields on dark backgrounds
   */
  value?: string;
  width?: CSSProperties["width"];
} & Partial<HTMLAttributes<HTMLTextAreaElement>>;

const TextArea: React.FC<TextAreaProps> = ({
  activeOnLoad = false,
  className: classes,
  helperText,
  errorMessage,
  height,
  id,
  textareaClasses,
  label,
  resize = "none",
  width,
  ...others
}) => {
  const [isActive, setIsActive] = useState(activeOnLoad);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const otherProps = { ...others, "aria-label": others["aria-label"] || label };

  useEffect(() => {
    if (textAreaRef.current && !isActive) {
      setIsActive(textAreaRef.current.value.length > 0);
    }
  }, [isActive, textAreaRef]);

  return (
    <FormField
      className={classNames(classes, { "form__field--active": isActive })}
    >
      {label ? (
        <FormElementLabel
          className="text-grey"
          hasError={!!errorMessage}
          label={label}
          htmlFor={id}
        />
      ) : (
        <></>
      )}

      <textarea
        className={classNames("py-xl px-sm", textareaClasses)}
        style={{ height: `${height}`, resize, width: `${width}` }}
        id={id}
        ref={textAreaRef}
        onFocus={() => setIsActive(true)}
        onBlur={() =>
          setIsActive(
            !!(
              textAreaRef.current?.value && textAreaRef.current.value.length > 0
            ),
          )
        }
        {...otherProps}
      />
      {helperText && !errorMessage ? <p>{helperText}</p> : <></>}
      {errorMessage ? (
        <FormElementErrorMessage message={errorMessage} />
      ) : (
        <></>
      )}
    </FormField>
  );
};

export default TextArea;
